import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { Lecture } from '../../../types/Lecture';
import { AlertTriangle, Edit3, Plus, Trash } from 'react-feather';
import { hapticsImpactLight } from '../../../utils/haptics';
import LectureEditorModal from '../LectureEditorModal';
import CustomModal from '../../Global/CustomModal';
import {
  addLectureToExam,
  deleteExamLecture,
  updateExamVerified,
  updateExamLecture,
  LectureParams,
} from '../../../services/exams';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { UserRole } from '../../../types/User';
import { RootState } from '../../../store/store';
import OraSwitch from '../../CustomMUI/Switch';
import { CircularProgress, FormControlLabel, Tooltip } from '@mui/material';
import {
  lectureReceived,
  lecturesSelectors,
} from '../../../slices/lectures/lecturesSlice';
import {
  addPendingLecture,
  pendingLecturesSelectors,
  removePendingLecture,
  updatePendingLecture,
} from '../../../slices/pendingLectures/pendingLecturesSlice';
import {
  MappedContentPayload,
  NameDatePayload,
  streamContentMapping,
} from '../../../services/ai';
import { enqueueSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import {
  convertDateToISOString,
  convertDateToMoment,
} from '../../../utils/dateConverter';
import LectureBulkUpload from '../LectureBulkUpload';

interface LectureScheduleEditorProps {
  setShouldRefreshStudySession?: (shouldRefreshStudySession: boolean) => void;
}

const LectureScheduleEditor: React.FC<LectureScheduleEditorProps> = ({
  setShouldRefreshStudySession,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const lectures = useSelector(lecturesSelectors.selectAll);
  const pendingLectures = useSelector(pendingLecturesSelectors.selectAll);

  // Ref to hold the latest pendingLectures
  const pendingLecturesRef = useRef(pendingLectures);

  // Update the ref whenever pendingLectures changes
  useEffect(() => {
    pendingLecturesRef.current = pendingLectures;
  }, [pendingLectures]);

  const [isLectureEditorModalOpen, setIsLectureEditorModalOpen] =
    useState(false);
  const [editingLecture, setEditingLecture] = useState<Lecture | null>(null);
  const [showConfirmDeleteLecture, setShowConfirmDeleteLecture] =
    useState<boolean>(false);
  const [lectureToDelete, setLectureToDelete] = useState<Lecture | null>(null);
  const [showConfirmVerifiedModal, setShowConfirmVerifiedModal] =
    useState(false);
  const [intendedVerifiedState, setIntendedVerifiedState] = useState<
    boolean | null
  >(null);

  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const managerPermissions =
    (currentUser?.role === UserRole.admin || currentUser?.ambassador) &&
    !currentUser?.demoMode;
  const readOnlyMode = Boolean(!managerPermissions && primaryExam?.verified);

  const lectureIdsBeingUpdated = pendingLectures
    .filter((p) => p.existingLectureId)
    .map((p) => p.existingLectureId);

  const handleMapFile = (
    s3Key: string,
    originalFilename: string,
    existingLectureId?: string
  ) => {
    const pendingId = uuidv4();
    const examId = primaryExam?.id;

    if (!examId) {
      console.error('Cannot map a lecture file without a primary exam.');
      return;
    }

    const existingLecture = lectures.find((l) => l.id === existingLectureId);
    const status = existingLecture ? 'UPDATING' : 'CREATING';

    dispatch(
      addPendingLecture({
        pendingId,
        status,
        name: existingLecture?.name || originalFilename,
        date: convertDateToISOString(existingLecture?.date),
        examId,
        originalFilename,
        s3Key,
        existingLectureId,
      })
    );

    const handleNameDateForPendingId = (payload: NameDatePayload) => {
      const existingLecture = lectures.find((l) => l.id === existingLectureId);
      const date =
        convertDateToISOString(existingLecture?.date) ||
        convertDateToISOString(payload.date);
      dispatch(
        updatePendingLecture({
          id: pendingId,
          name: payload.name,
          date,
        })
      );
    };

    const handleMappedContentForPendingId = async (
      payload: MappedContentPayload
    ) => {
      if (payload.vignettes.length === 0 && payload.flashcards.length === 0) {
        enqueueSnackbar(`No content found for lecture: ${originalFilename}`, {
          autoHideDuration: 3000,
        });
      }
      const currentPendingLectures = pendingLecturesRef.current;
      const pendingLecture = currentPendingLectures.find(
        (p) => p.id === pendingId
      );
      const existingLecture = lectures.find((l) => l.id === existingLectureId);

      if (!pendingLecture) {
        console.error(
          `[${pendingId}] Pending lecture not found in current state`
        );
        enqueueSnackbar(`Something went wrong. Please try again.`, {
          variant: 'error',
        });
        return;
      }
      const date =
        convertDateToMoment(existingLecture?.date) ||
        convertDateToMoment(pendingLecture.date);
      const finalLectureData: LectureParams = {
        name: pendingLecture.name || existingLecture?.name || originalFilename,
        date,
        vignetteIds: payload.vignettes,
        flashcardIds: payload.flashcards,
        s3Key: pendingLecture.s3Key,
      };

      try {
        let updatedLecture;
        if (existingLecture) {
          updatedLecture = await updateExamLecture(
            examId,
            existingLecture.id,
            finalLectureData
          );
        } else {
          updatedLecture = await addLectureToExam(examId, finalLectureData);
        }

        if (!updatedLecture) {
          console.error(
            `[${pendingId}] Failed to ${existingLecture ? 'update' : 'create'} lecture`
          );
          return;
        }

        dispatch(lectureReceived(updatedLecture));
        dispatch(removePendingLecture(pendingId));

        enqueueSnackbar(
          `Lecture "${updatedLecture.name}" ${existingLecture ? 'updated' : 'added'} successfully.`,
          {
            autoHideDuration: 3000,
          }
        );
      } catch (apiError) {
        console.error(`[${pendingId}] Failed to save final lecture:`, apiError);
        enqueueSnackbar(`Error saving lecture: ${originalFilename}`);
        dispatch(
          updatePendingLecture({
            id: pendingId,
            status: 'FAILED',
            error: 'Failed to save lecture data.',
          })
        );
      }
    };

    const handleMappingErrorForPendingId = (error: Event | Error) => {
      // Allow Error type too
      console.error(`[${pendingId}] Mapping Error:`, error);
      enqueueSnackbar(`Error processing lecture: ${originalFilename}`, {
        autoHideDuration: 5000,
      });
      // Update pending state to FAILED
      dispatch(
        updatePendingLecture({
          id: pendingId,
          status: 'FAILED',
          error:
            error instanceof Error
              ? error.message
              : 'An unknown mapping error occurred.',
        })
      );
    };

    try {
      const input = {
        type: 'fileKey',
        value: s3Key,
        examDate: primaryExam?.examDate,
      };
      streamContentMapping(
        input,
        handleNameDateForPendingId,
        handleMappedContentForPendingId,
        handleMappingErrorForPendingId
      );
    } catch (error) {
      console.error(`[${pendingId}] Failed to initiate mapping stream:`, error);
      enqueueSnackbar(`Failed to start processing: ${originalFilename}`, {
        variant: 'error',
      });
      dispatch(
        updatePendingLecture({
          id: pendingId,
          status: 'FAILED',
          error:
            error instanceof Error
              ? error.message
              : 'Failed to initiate streaming.',
        })
      );
    }
  };

  const handleLectureSave = (lecture: Lecture) => {
    if (readOnlyMode) {
      console.error('Cannot save lecture in read-only mode');
      return;
    }

    if (!primaryExam) {
      console.error('Primary exam not found');
      return;
    }

    if (primaryExam.type !== 'CUSTOM') {
      console.error('Primary exam is not a custom exam');
      return;
    }

    const lectureIndex = lectures.findIndex((l) => l.id === lecture.id);
    if (lectureIndex !== -1) {
      updateExamLecture(primaryExam.id, lecture.id, {
        name: lecture.name,
        date: lecture.date ? moment.utc(lecture.date) : undefined,
        vignetteIds: lecture.vignetteIds,
        flashcardIds: lecture.flashcardIds,
        s3Key: lecture.s3Key,
      });
    } else {
      addLectureToExam(primaryExam.id, {
        name: lecture.name,
        date: lecture.date ? moment.utc(lecture.date) : undefined,
        vignetteIds: lecture.vignetteIds,
        flashcardIds: lecture.flashcardIds,
        s3Key: lecture.s3Key,
      });
    }
    setShouldRefreshStudySession?.(true);
  };

  const handleConfirmDeleteLecture = () => {
    if (readOnlyMode) {
      console.error('Cannot delete lecture in read-only mode');
      return;
    }

    if (!primaryExam) {
      console.error('Primary exam not found');
      return;
    }

    if (!lectureToDelete) {
      console.error('Lecture to delete not found');
      return;
    }

    deleteExamLecture(primaryExam.id, lectureToDelete.id);
    setShowConfirmDeleteLecture(false);
    setLectureToDelete(null);
    setShouldRefreshStudySession?.(true);
  };

  const handleEditClick = (lecture: Lecture) => {
    if (
      !lecture.vignetteIds?.length &&
      !lecture.flashcardIds?.length &&
      readOnlyMode
    ) {
      return;
    }
    setEditingLecture(lecture);
    setIsLectureEditorModalOpen(true);
  };

  const handleModalClose = () => {
    setIsLectureEditorModalOpen(false);
    setEditingLecture(null);
  };

  const handleConfirmVerifiedToggle = () => {
    setShowConfirmVerifiedModal(false);
    if (primaryExam?.id && intendedVerifiedState !== null) {
      updateExamVerified(primaryExam.id, intendedVerifiedState);
    }
    setIntendedVerifiedState(null);
    hapticsImpactLight();
  };

  return (
    <div className="edit-lecture-schedule">
      <div className="edit-lecture-schedule_header">
        <h2>Lecture Schedule{readOnlyMode && ' Preview'}</h2>
        {managerPermissions && (
          <FormControlLabel
            control={
              <OraSwitch
                checked={!!primaryExam?.verified}
                onChange={(e) => {
                  if (primaryExam?.id) {
                    setIntendedVerifiedState(e.target.checked);
                    setShowConfirmVerifiedModal(true);
                    hapticsImpactLight();
                  }
                }}
                name="verified"
              />
            }
            label="Publish"
            sx={{
              marginLeft: 'auto',
              '& .MuiFormControlLabel-label': {
                marginLeft: '8px',
                color: 'var(--type-body-inverse)',
              },
            }}
          />
        )}
        {!readOnlyMode && (
          <div
            className="button button--glass button--add-lecture"
            role="button"
            tabIndex={0}
            onClick={() => {
              setShowBulkUploadModal(true);
              hapticsImpactLight();
            }}
          >
            <Plus />
            <span>Add Lectures</span>
          </div>
        )}
      </div>
      <div
        className={`lecture-list ${lectures.length === 0 ? 'showing-placeholder' : ''}`}
      >
        {lectures.length === 0 && (
          <div className="lecture-list_placeholder">Add lectures...</div>
        )}
        <div className="lecture-list_lectures">
          {pendingLectures
            .filter((p) => p.status === 'CREATING')
            .map((pendingLecture) => (
              <div
                className="lecture-list_lectures_item lecture-list_lectures_item--pending"
                key={pendingLecture.id || pendingLecture.originalFilename}
                style={{ cursor: 'default' }}
              >
                <div className="lecture-list_lectures_item_info">
                  <span className="lecture-list_lectures_item_info_title">
                    {pendingLecture.name === 'lecture_content.txt'
                      ? 'Mapping your text...'
                      : pendingLecture.name || pendingLecture.originalFilename}
                  </span>
                  <span className="lecture-list_lectures_item_info_date">
                    {pendingLecture.date
                      ? moment(pendingLecture.date).format('MMMM D, YYYY')
                      : ''}
                  </span>
                </div>
                <div className="lecture-list_lectures_item_actions">
                  <div className="spinner">
                    <CircularProgress
                      sx={{
                        color: 'var(--type-body-inverse)',
                        height: '1rem !important',
                        width: '1rem !important',
                      }}
                    />
                  </div>
                  <button
                    className="button button--glass button--remove-lecture"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(removePendingLecture(pendingLecture.id));
                      hapticsImpactLight();
                    }}
                    aria-label={`Delete lecture ${pendingLecture.originalFilename}`}
                  >
                    <Trash />
                  </button>
                </div>
              </div>
            ))}
          {lectures.map((lecture) => (
            <div
              className={`lecture-list_lectures_item ${!lecture.date ? 'no-date' : ''} ${
                !lecture.vignetteIds?.length &&
                !lecture.flashcardIds?.length &&
                'no-content'
              } ${
                lectureIdsBeingUpdated.includes(lecture.id)
                  ? 'lecture-list_lectures_item--pending'
                  : ''
              }`}
              key={lecture.id || lecture.name}
              onClick={() => {
                handleEditClick(lecture);
                hapticsImpactLight();
              }}
            >
              <div className="lecture-list_lectures_item_info">
                <span className="lecture-list_lectures_item_info_title">
                  {lecture.name}
                  {!lecture.vignetteIds?.length &&
                    !lecture.flashcardIds?.length && (
                      <Tooltip title="This lecture has no content.">
                        <AlertTriangle style={{ color: 'var(--error)' }} />
                      </Tooltip>
                    )}
                </span>
                <span className="lecture-list_lectures_item_info_date">
                  {lecture.date
                    ? moment(lecture.date).format('MMMM D, YYYY')
                    : 'No date selected'}
                </span>
                {lecture.description && (
                  <span className="lecture-list_lectures_item_info_description">
                    {lecture.description}
                  </span>
                )}
              </div>
              {!readOnlyMode && (
                <div className="lecture-list_lectures_item_actions">
                  {lectureIdsBeingUpdated.includes(lecture.id) ? (
                    <div className="spinner">
                      <CircularProgress
                        sx={{
                          color: 'var(--type-body-inverse)',
                          height: '1rem !important',
                          width: '1rem !important',
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      className="button button--glass button--edit-lecture"
                      onClick={() => handleEditClick(lecture)}
                      aria-label={`Edit lecture ${lecture.name}`}
                    >
                      <Edit3 />
                    </button>
                  )}
                  <button
                    className="button button--glass button--remove-lecture"
                    onClick={(e) => {
                      e.stopPropagation(); // Stop event from bubbling up and opening the lecture editor
                      setLectureToDelete(lecture);
                      setShowConfirmDeleteLecture(true);
                      hapticsImpactLight();
                    }}
                    aria-label={`Delete lecture ${lecture.name}`}
                  >
                    <Trash />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="lecture-list_exam-date">
          <div className="lecture-list_exam-date_date">
            {primaryExam?.examDate
              ? moment(primaryExam?.examDate).format('MMMM D, YYYY')
              : 'No Exam Date'}
          </div>
          {primaryExam?.examDate && (
            <div className="lecture-list_exam-date_label">Exam Date</div>
          )}
        </div>
      </div>
      {isLectureEditorModalOpen && (
        <LectureEditorModal
          readOnlyMode={readOnlyMode}
          handleClose={handleModalClose}
          handleLectureSave={handleLectureSave}
          editLecture={editingLecture || undefined}
          handleMapFile={handleMapFile}
        />
      )}
      {showBulkUploadModal && (
        <LectureBulkUpload
          handleMapFile={handleMapFile}
          handleClose={() => setShowBulkUploadModal(false)}
        />
      )}
      <CustomModal
        open={showConfirmDeleteLecture}
        onClose={() => {
          setShowConfirmDeleteLecture(false);
          setLectureToDelete(null);
        }}
        name="delete-lecture"
        glass={true}
        blurs={true}
        blurType="error"
      >
        <div className="modal_header">
          <h2>Delete Lecture</h2>
        </div>
        <div className="modal_content">
          <p>
            Are you sure you want to delete the lecture "{lectureToDelete?.name}
            "? This action cannot be undone.
          </p>
        </div>
        <div className="modal_actions">
          <button
            className="button button--glass"
            onClick={() => {
              setShowConfirmDeleteLecture(false);
              setLectureToDelete(null);
              hapticsImpactLight();
            }}
          >
            Cancel
          </button>
          <button
            className="button button--error"
            onClick={() => {
              handleConfirmDeleteLecture();
              hapticsImpactLight();
            }}
          >
            Delete
          </button>
        </div>
      </CustomModal>
      <CustomModal
        open={showConfirmVerifiedModal}
        onClose={() => {
          setShowConfirmVerifiedModal(false);
          setIntendedVerifiedState(null);
        }}
        name="confirm-verified-toggle"
        glass={true}
        blurs={true}
      >
        <div className="modal_header">
          <h2>⚠️ {intendedVerifiedState ? 'Publish' : 'Unpublish'} Exam</h2>
        </div>
        <div className="modal_content">
          <p className="m-t-0">
            Are you sure you want to{' '}
            {intendedVerifiedState ? 'publish' : 'unpublish'} this exam?
          </p>
          {intendedVerifiedState ? (
            <p className="m-b-0">
              Publishing this exam will make it available to all students at
              your school.
            </p>
          ) : (
            <>
              <p>
                Unpublishing this exam will make it unavailable to students at
                your school.
              </p>
              <p className="m-b-0">
                If students are already studying it, they will lose access.
              </p>
            </>
          )}
        </div>
        <div className="modal_actions">
          <button
            className="button button--glass"
            onClick={() => {
              setShowConfirmVerifiedModal(false);
              setIntendedVerifiedState(null);
              hapticsImpactLight();
            }}
          >
            Cancel
          </button>
          <button
            className="button button--glass"
            onClick={handleConfirmVerifiedToggle}
          >
            Confirm
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default LectureScheduleEditor;
